import { useState, useCallback, useEffect, useRef } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useLog } from '../../contexts/LogContext';
import { api } from '../../utils/api';
import { UserSearch } from '../../components/UserSearch';
import { CreditBalanceCard } from '../../components/CreditBalanceCard';
import { CreditActionForm } from '../../components/CreditActionForm';
import { TransactionHistory } from '../../components/TransactionHistory';
import type {
  AdminUser,
  AdminUsersResponse,
  CreditBalance,
  CreditTransaction,
} from '../../types/credits.types';
import type { UserCreditBalanceResponse } from './types';

export const CreditsContainer = () => {
  const { token } = useConfig();
  const { log } = useLog();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [creditAmount, setCreditAmount] = useState('');
  const [resetTotal, setResetTotal] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedUser = users.find((user) => user.id === selectedUserId);
  const selectedOrganizationName = selectedUser?.organizationName ?? '';

  const searchUsers = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setUsers([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await api<AdminUsersResponse>(
          'GET',
          `/api/admin/users?search=${encodeURIComponent(query)}&limit=20`,
          undefined,
          token,
        );

        if (response.status === 200) {
          setUsers(response.data.data);
        } else {
          log('err', `Failed to search users: ${response.status}`);
        }
      } catch (error) {
        log('err', `Error searching users: ${(error as Error).message}`);
      } finally {
        setSearchLoading(false);
      }
    },
    [token, log],
  );

  const fetchUserCredits = useCallback(
    async (userId: number) => {
      try {
        const [balanceResponse, transactionsResponse] = await Promise.all([
          api<CreditBalance>(
            'GET',
            `/api/credits/user/${userId}/balance`,
            undefined,
            token,
          ),
          api<CreditTransaction[]>(
            'GET',
            `/api/credits/user/${userId}/transactions`,
            undefined,
            token,
          ),
        ]);

        if (balanceResponse.status === 200) {
          setBalance(balanceResponse.data);
        }
        if (transactionsResponse.status === 200) {
          setTransactions(transactionsResponse.data);
        }
      } catch (error) {
        log('err', `Error fetching credits: ${(error as Error).message}`);
      }
    },
    [token, log],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        searchUsers(query);
      }, 300);
    },
    [searchUsers],
  );

  const handleUserSelect = useCallback(
    (userId: number) => {
      setSelectedUserId(userId);
      setBalance(null);
      setTransactions([]);
      setCreditAmount('');
      setResetTotal('');
      setCreditDescription('');
      fetchUserCredits(userId);
    },
    [fetchUserCredits],
  );

  const handleAddCredits = useCallback(async () => {
    if (!selectedUserId || actionLoading) {
      return;
    }

    const parsedAmount = parseInt(creditAmount, 10);
    if (isNaN(parsedAmount) || parsedAmount < 1) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await api<UserCreditBalanceResponse>(
        'POST',
        `/api/credits/user/${selectedUserId}/add`,
        {
          amount: parsedAmount,
          description: creditDescription || undefined,
        },
        token,
      );

      if (response.status === 201) {
        log(
          'ok',
          `Added ${parsedAmount} credits to organization "${selectedOrganizationName}"`,
        );
        setCreditAmount('');
        setCreditDescription('');
        await fetchUserCredits(selectedUserId);
      } else {
        log('err', `Failed to add credits: ${response.status}`);
      }
    } catch (error) {
      log('err', `Error adding credits: ${(error as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  }, [
    selectedUserId,
    actionLoading,
    creditAmount,
    creditDescription,
    token,
    log,
    fetchUserCredits,
  ]);

  const handleDeductCredits = useCallback(async () => {
    if (!selectedUserId || actionLoading) {
      return;
    }

    const parsedAmount = parseInt(creditAmount, 10);
    if (isNaN(parsedAmount) || parsedAmount < 1) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await api<UserCreditBalanceResponse>(
        'POST',
        `/api/credits/user/${selectedUserId}/deduct`,
        {
          amount: parsedAmount,
          description: creditDescription || undefined,
        },
        token,
      );

      if (response.status === 201) {
        log(
          'ok',
          `Deducted ${parsedAmount} credits from organization "${selectedOrganizationName}"`,
        );
        setCreditAmount('');
        setCreditDescription('');
        await fetchUserCredits(selectedUserId);
      } else {
        log('err', `Failed to deduct credits: ${response.status}`);
      }
    } catch (error) {
      log('err', `Error deducting credits: ${(error as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  }, [
    selectedUserId,
    actionLoading,
    creditAmount,
    creditDescription,
    token,
    log,
    fetchUserCredits,
  ]);

  const handleResetCredits = useCallback(async () => {
    if (!selectedUserId || actionLoading) {
      return;
    }

    const parsedTotal = parseInt(resetTotal, 10);
    if (isNaN(parsedTotal) || parsedTotal < 0) {
      return;
    }

    const confirmed = window.confirm(
      `Reset total credits of organization "${selectedOrganizationName}" to ${parsedTotal}? Removed credits count as used first and cannot be restored.`,
    );
    if (!confirmed) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await api<UserCreditBalanceResponse | { message?: string }>(
        'POST',
        `/api/credits/user/${selectedUserId}/reset`,
        {
          totalCredits: parsedTotal,
          description: creditDescription || undefined,
        },
        token,
      );

      if (response.status === 201) {
        log(
          'ok',
          `Reset total to ${parsedTotal} for organization "${selectedOrganizationName}"`,
        );
        setResetTotal('');
        setCreditDescription('');
        await fetchUserCredits(selectedUserId);
      } else {
        // The api helper hands back plain text when the body is not JSON, so guard before using `in`.
        const message =
          typeof response.data === 'object' &&
          response.data !== null &&
          'message' in response.data &&
          response.data.message
            ? `: ${response.data.message}`
            : '';
        log('err', `Failed to reset credits: ${response.status}${message}`);
      }
    } catch (error) {
      log('err', `Error resetting credits: ${(error as Error).message}`);
    } finally {
      setActionLoading(false);
    }
  }, [
    selectedUserId,
    actionLoading,
    resetTotal,
    creditDescription,
    selectedOrganizationName,
    token,
    log,
    fetchUserCredits,
  ]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div>
      <UserSearch
        users={users}
        selectedUserId={selectedUserId}
        searchQuery={searchQuery}
        loading={searchLoading}
        onSearchChange={handleSearchChange}
        onUserSelect={handleUserSelect}
      />
      {selectedUserId && (
        <>
          <CreditBalanceCard
            balance={balance}
            organizationName={selectedOrganizationName}
          />
          <CreditActionForm
            amount={creditAmount}
            resetTotal={resetTotal}
            description={creditDescription}
            loading={actionLoading}
            onAmountChange={setCreditAmount}
            onResetTotalChange={setResetTotal}
            onDescriptionChange={setCreditDescription}
            onAddCredits={handleAddCredits}
            onDeductCredits={handleDeductCredits}
            onResetCredits={handleResetCredits}
          />
          <TransactionHistory transactions={transactions} />
        </>
      )}
    </div>
  );
};
